import { InputGroup, InputGroupAddon, InputGroupInput } from "@/ds/shadcn/components/ui/input-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ds/shadcn/components/ui/tabs";
import type { CollectionModel } from "@/features/collections/api/models/CollectionModel";
import { useCollections } from "@/features/collections/api/useCollections";
import { DataView } from "@/features/common/components/dataview/DataView";
import { GalleryItem } from "@/features/common/components/dataview/GalleryItem";
import { SearchResults } from "@/features/common/components/SearchResults";
import { VariantSelector } from "@/features/common/components/VariantSelector";
import type { GetProductAndVariantsMarketPriceResponseModel, VariantMarketPriceResponseModel } from "@/features/products/api/useProduct";
import { PriceAlertTargetOptions, type PriceAlertTarget } from "@/features/priceAlerts/PriceAlertTarget";
import { useDebouncedState } from "@/lib/common/useDebouncedState";
import { collectionArts } from "@/lib/media/collectionArts";
import { SearchIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

interface CollectionSelectorProps {
	onSelect: (collection: CollectionModel) => void;
	onCancel: () => void;
	scrollRef: React.RefObject<HTMLDivElement | null>;
}

const CollectionSelector = (props: CollectionSelectorProps) => {
	const {
		onSelect,
		onCancel,
		scrollRef
	} = props;

	const [debouncedFilterQuery, filterQuery, setFilterQuery] = useDebouncedState("", 500);
	const collections = useCollections("Alphabetical", debouncedFilterQuery || null);
	const additionalProps = useMemo(() => ({ onSelect }), [onSelect]);

	return (
		<>
			<InputGroup className="bg-background border-b p-3">
				<InputGroupAddon>
					<SearchIcon />
				</InputGroupAddon>
				<InputGroupInput placeholder="Search collections" value={filterQuery} onChange={e => setFilterQuery(e.currentTarget.value)} />
			</InputGroup>

			<SearchResults searchQuery={filterQuery} forQuery={collections} onCancel={onCancel}>
				<DataView
					forQuery={collections}
					getKey={x => x.collectionId}
					scrollRef={scrollRef}
					galleryMinWidth={192}
					galleryComponent={CollectionSelectorDataViewGalleryItem}
					additionalProps={additionalProps} />
			</SearchResults>
		</>
	);
};

interface CollectionSelectorDataViewProps extends CollectionModel {
	onSelect: (collection: CollectionModel) => void;
}

const CollectionSelectorDataViewGalleryItem = (props: CollectionSelectorDataViewProps) => {
	const {
		name,
		description,
		image,
		onSelect
	} = props;

	return (
		<GalleryItem
			title={name}
			description={description}
			imageUrl={collectionArts[image]}
			isProductImage={false}
			onClick={() => onSelect(props)} />
	);
};

export interface PriceAlertsTargetSelectorProps {
	onSelectProduct: (product: GetProductAndVariantsMarketPriceResponseModel, variant: VariantMarketPriceResponseModel) => void;
	onSelectCollection: (collection: CollectionModel, targetType: "Collection" | "CollectionVariants") => void;
	onCancel: () => void;
	scrollRef: React.RefObject<HTMLDivElement | null>;
}

export const PriceAlertsTargetSelector = (props: PriceAlertsTargetSelectorProps) => {
	const {
		onSelectProduct,
		onSelectCollection,
		onCancel,
		scrollRef
	} = props;

	const [priceAlertTarget, setPriceAlertTarget] = useState<PriceAlertTarget>("Variant");

	const onCollectionSelect = useCallback((collection: CollectionModel) => {
		if (priceAlertTarget === "Variant") {
			return;
		}

		onSelectCollection(collection, priceAlertTarget);
	}, [onSelectCollection, priceAlertTarget]);

	return (
		<Tabs className="gap-0" value={priceAlertTarget} onValueChange={value => setPriceAlertTarget(value as PriceAlertTarget)}>
			<div className="p-3">
				<TabsList className="w-full">
					{
						PriceAlertTargetOptions.map(x => (
							<TabsTrigger key={x.value} value={x.value}>{x.label}</TabsTrigger>
						))
					}
				</TabsList>
			</div>

			<TabsContent value="Variant">
				<div className="type-description border-t border-b p-3">
					Tracks the price of a single product.
				</div>

				<VariantSelector onSelect={onSelectProduct} onCancel={onCancel} scrollRef={scrollRef} />
			</TabsContent>

			<TabsContent value="CollectionVariants">
				<div className="type-description border-t border-b p-3">
					Applies this alert to every product currently in a collection. You&apos;ll be notified about any product that crosses the threshold.
				</div>

				<CollectionSelector onSelect={onCollectionSelect} onCancel={onCancel} scrollRef={scrollRef} />
			</TabsContent>

			<TabsContent value="Collection">
				<div className="type-description border-t border-b p-3">
					Tracks the total combined value of a whole collection.
				</div>

				<CollectionSelector onSelect={onCollectionSelect} onCancel={onCancel} scrollRef={scrollRef} />
			</TabsContent>
		</Tabs>
	);
};
